import { useState, useEffect, useRef } from 'react';
import { Loader2, ChevronDown, Video, Search, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@clerk/clerk-react';
import { useRevit } from '../../contexts/RevitContext';
import './AddVideoTracking.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const VPS_URL = process.env.REACT_APP_VPS_URL;

export const AddVideoTracking = ({ activeOffer, noCollapse = false }) => {
  const { username } = useRevit();
  const { getToken } = useAuth();
  const [videos, setVideos] = useState([]);
  const [selectedVideoIds, setSelectedVideoIds] = useState([]);
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);
  const [isVideoDropdownOpen, setIsVideoDropdownOpen] = useState(false);
  const [isAddingToAll, setIsAddingToAll] = useState(false);
  const [isAddingToSelected, setIsAddingToSelected] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [videoSearch, setVideoSearch] = useState('');
  const hasBeenManuallyToggled = useRef(false);
  const dropdownRef = useRef(null);

  // Reset state when active offer changes
  useEffect(() => {
    hasBeenManuallyToggled.current = false;
    const allVerified = !!(activeOffer?.landing_verified_at && activeOffer?.thank_you_verified_at);
    setIsOpen(allVerified);
    setIsVideoDropdownOpen(false);
    setSelectedVideoIds([]);
    setVideoSearch('');
  }, [activeOffer?.id]);

  // If steps become verified in-session without a manual toggle, auto-open
  const allCoreStepsVerified = !!(activeOffer?.landing_verified_at && activeOffer?.thank_you_verified_at);
  useEffect(() => {
    if (!hasBeenManuallyToggled.current && allCoreStepsVerified) {
      setIsOpen(true);
    }
  }, [allCoreStepsVerified]);

  // Prefill landing page URL from activeOffer
  useEffect(() => {
    if (activeOffer?.landing_url) {
      setLandingPageUrl(activeOffer.landing_url);
    }
  }, [activeOffer?.landing_url]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isVideoDropdownOpen) return;
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsVideoDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isVideoDropdownOpen]);

  const handleToggle = () => {
    hasBeenManuallyToggled.current = true;
    setIsOpen(prev => !prev);
  };

  const fetchAllVideos = async () => {
    if (videos.length > 0) {
      setIsVideoDropdownOpen(prev => !prev);
      return;
    }

    setIsLoadingVideos(true);
    setIsVideoDropdownOpen(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `https://${SERVER_URL}/api/get-all-videos/${username}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setVideos(response.data.videos);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setIsLoadingVideos(false);
    }
  };

  const toggleVideoSelection = (videoId) => {
    setSelectedVideoIds(prev =>
      prev.includes(videoId)
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };

  const handleAddTrackingToAll = async () => {
    if (!landingPageUrl) return;

    setIsAddingToAll(true);
    try {
      const token = await getToken();
      const response = await axios.put(
        `https://${VPS_URL}/api/add-tracking-to-videos`,
        { userId: username, url: landingPageUrl },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      alert(response.data.message || 'Tracking added to all videos successfully');
    } catch (error) {
      console.error('Error adding tracking to all videos:', error);
      alert('Error adding tracking to all videos');
    } finally {
      setIsAddingToAll(false);
    }
  };

  const handleAddTrackingToSelected = async () => {
    if (!landingPageUrl || selectedVideoIds.length === 0) return;

    setIsAddingToSelected(true);
    try {
      const token = await getToken();
      const response = await axios.put(
        `https://${VPS_URL}/api/add-tracking-to-selected-videos`,
        { userId: username, videoIds: selectedVideoIds, url: landingPageUrl },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      alert(response.data.message || 'Tracking added to selected videos successfully');
      setSelectedVideoIds([]);
      setIsVideoDropdownOpen(false);
    } catch (error) {
      console.error('Error adding tracking to selected videos:', error);
      alert('Error adding tracking to selected videos');
    } finally {
      setIsAddingToSelected(false);
    }
  };

  if (!activeOffer && !noCollapse) return null;

  const filteredVideos = videos.filter(v =>
    v.title.toLowerCase().includes(videoSearch.toLowerCase())
  );

  // ── Shared body content ──────────────────────────────────
  const body = (
    <div className={`fm-video-tracking-body${noCollapse ? ' fm-video-tracking-body--no-collapse' : ''}`}>

      {/* URL input */}
      <div className="fm-video-tracking-url-input">
        <label htmlFor="avt-landing-url" className="fm-video-tracking-label">
          Landing page URL
          <span className="fm-video-tracking-hint">Must already be linked in your video descriptions</span>
          <span className="fm-video-tracking-hint">Note: You may use multiple landing pages for the same offer. Simply run this operation for all your landing pages by changing the link here.</span>

        </label>
        <input
          id="avt-landing-url"
          type="url"
          className="fm-video-tracking-input"
          placeholder="https://example.com"
          value={landingPageUrl}
          onChange={(e) => setLandingPageUrl(e.target.value)}
        />
      </div>

      {/* Select + add row */}
      <div className="fm-vt-select-group">
        <div className="fm-video-dropdown-wrapper" ref={dropdownRef}>
          <button
            className={`fm-video-dropdown-trigger${isVideoDropdownOpen ? ' fm-video-dropdown-trigger--open' : ''}`}
            onClick={fetchAllVideos}
            disabled={isLoadingVideos}
            aria-haspopup="listbox"
            aria-expanded={isVideoDropdownOpen}
          >
            {isLoadingVideos ? (
              <><Loader2 size={13} className="animate-spin" style={{ marginRight: 6 }} />Loading…</>
            ) : (
              <>
                <span className="fm-vt-trigger-label">
                  {selectedVideoIds.length > 0
                    ? <><CheckCircle2 size={13} className="fm-vt-check-icon" />{selectedVideoIds.length} video{selectedVideoIds.length > 1 ? 's' : ''} selected</>
                    : 'Select videos'
                  }
                </span>
                <ChevronDown
                  size={14}
                  className={`fm-video-dropdown-chevron${isVideoDropdownOpen ? ' fm-video-dropdown-chevron--open' : ''}`}
                />
              </>
            )}
          </button>

          {isVideoDropdownOpen && (
            <div className="fm-video-dropdown-menu" role="listbox">
              <div className="fm-video-search-wrap">
                <Search size={13} className="fm-video-search-icon" />
                <input
                  type="text"
                  className="fm-video-search-input"
                  placeholder="Search videos…"
                  value={videoSearch}
                  onChange={e => setVideoSearch(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  autoFocus
                />
                {videoSearch && (
                  <button
                    className="fm-video-search-clear"
                    onClick={() => setVideoSearch('')}
                    aria-label="Clear search"
                  >×</button>
                )}
              </div>
              {videos.length === 0 ? (
                <div className="fm-video-dropdown-empty">Loading videos…</div>
              ) : filteredVideos.length === 0 ? (
                <div className="fm-video-dropdown-empty">No videos match "{videoSearch}"</div>
              ) : (
                filteredVideos.map(video => {
                  const isSelected = selectedVideoIds.includes(video.id);
                  return (
                    <div
                      key={video.id}
                      role="option"
                      aria-selected={isSelected}
                      className={`fm-video-dropdown-item${isSelected ? ' fm-video-dropdown-item--selected' : ''}`}
                      onClick={() => toggleVideoSelection(video.id)}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="fm-video-checkbox"
                        tabIndex={-1}
                      />
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="fm-video-thumbnail"
                      />
                      <span className="fm-video-title">{video.title}</span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        <button
          className="fm-add-tracking-btn"
          disabled={selectedVideoIds.length === 0 || isAddingToSelected || !landingPageUrl}
          onClick={handleAddTrackingToSelected}
        >
          {isAddingToSelected ? (
            <><Loader2 size={13} className="animate-spin" style={{ marginRight: 6 }} />Adding…</>
          ) : (
            'Add tracking'
          )}
        </button>
      </div>

      <div className="fm-vt-divider"><span>or add to all videos</span></div>

      {/* Warning note */}
      <p className="fm-vt-all-warning">
        ⚠️ We recommend testing on a single video first before using this. Only proceed once you've confirmed the tracking link was added correctly.
      </p>

      {/* All videos row */}
      <div className="fm-vt-row">
        <div className="fm-vt-row-label">
          <span className="fm-vt-row-title">All videos</span>
          <span className="fm-vt-row-desc">Append tracking to every video in your channel</span>
        </div>
        <button
          className="fm-add-tracking-btn fm-add-tracking-btn--ghost"
          onClick={handleAddTrackingToAll}
          disabled={isAddingToAll || !landingPageUrl}
        >
          {isAddingToAll ? (
            <><Loader2 size={13} className="animate-spin" style={{ marginRight: 6 }} />Adding…</>
          ) : (
            'Add to all'
          )}
        </button>
      </div>
    </div>
  );

  // ── No-collapse mode (used inside tab panel) ─────────────
  if (noCollapse) {
    return <div className="fm-video-tracking-tab-panel">{body}</div>;
  }

  // ── Collapsible mode ──────────────────────────────────────
  return (
    <div className={`fm-video-tracking-section${isOpen ? ' fm-video-tracking-section--open' : ''}`}>
      <button
        className="fm-video-tracking-header"
        onClick={handleToggle}
        type="button"
        aria-expanded={isOpen}
      >
        <div className="fm-video-tracking-header-left">
          <Video size={15} className="fm-video-tracking-icon" />
          <span className="fm-video-tracking-title">Add Tracking Links to Your Videos</span>
        </div>
        <ChevronDown
          size={16}
          className={`fm-video-tracking-chevron${isOpen ? ' fm-video-tracking-chevron--open' : ''}`}
        />
      </button>

      {isOpen && body}
    </div>
  );
};
